/**
 * The check-before-adding screen in plain words (Figma 10c): how the studio's own work words were
 * read ("Karigar → Weaver (K)") and one line per row that waits in To check.
 */

export type WorkWord = { word: string; work: string; prefix: string };

type Category = { id: string; prefix: string; name?: string; userRole?: string | null };

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const ROLE: Record<string, string> = { WEAVER: "Weaver", HELPER: "Helper", DYER: "Dyer", TAANI: "Warping" };

/** The plain work a type stands for: its role, or the English in brackets ("Karigar (weaver)" → Weaver), or its name. */
export const workName = (c: Category): string => {
  if (c.userRole && ROLE[c.userRole]) return ROLE[c.userRole];
  const inBrackets = (c.name || "").match(/\(([^)]+)\)/);
  return inBrackets ? cap(inBrackets[1].trim()) : c.name || c.prefix;
};

/** Each distinct word from the Work column (or the name) and the work type it was read as. */
export const workWordsRead = (rows: any[], categories: Category[]): WorkWord[] => {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const byPrefix = new Map(categories.map((c) => [c.prefix, c]));
  const seen = new Map<string, WorkWord>();
  for (const r of rows) {
    const word = String(r.rawWork || "").trim();
    if (!word || r.status === "SKIPPED") continue;
    const cat = (r.categoryId && byId.get(r.categoryId)) || (r.prefix && byPrefix.get(r.prefix));
    if (!cat) continue;
    const key = word.toLowerCase();
    if (!seen.has(key)) seen.set(key, { word: cap(word), work: workName(cat), prefix: cat.prefix });
  }
  return Array.from(seen.values());
};

/** "Karigar → Weaver (K), Jodiya → Helper (J)" */
export const workWordsLine = (words: WorkWord[]): string => words.map((w) => `${w.word} → ${w.work} (${w.prefix})`).join(", ");

export type CheckLine = { row: number; text: string; vars: Record<string, string> };

/**
 * One line per row to check, its problems joined: what a supervisor would say aloud. The text is a
 * translation key with {{name}} / {{word}} / {{code}} filled from the row.
 */
export const rowsToCheck = (rows: any[]): CheckLine[] =>
  rows
    .filter((r) => r.status === "NEEDS_REVIEW")
    .map((r) => {
      const name = String(r.rawName || "").trim();
      const issues: string[] = r.issues || [];
      const matched = (r.matchedWorkers || [])[0];
      const vars = { name: name || "—", word: cap(String(r.rawWork || "").trim()), code: matched?.workerCode || matched?.code || String(r.rawCode || "") };
      const say = (code: string): string => {
        switch (code) {
          case "DUPLICATE_NAME_IN_FILE":
            return "“{{name}}” is in the list twice";
          case "NAME_IN_HKP":
            return vars.code ? "“{{name}}” is already in HKP as {{code}}" : "“{{name}}” is already in HKP";
          case "UNKNOWN_WORK":
            return "work “{{word}}” not known: pick their work";
          case "NO_CATEGORY":
            return "no work given: pick their work";
          case "BLANK_NAME":
            return "a row with no name";
          case "CODE_EXISTS":
            return "code {{code}} is already in HKP";
          case "DUPLICATE_CODE_IN_FILE":
            return "code {{code}} is in the list twice";
          default:
            return code;
        }
      };
      return { row: Number(r.rowNo) || 0, text: issues.map(say).join(" · "), vars };
    });
